/**
 * NodeSelector — výběr ACM nodu pro multi-node operace.
 *
 * Zobrazí registrované nody z ccp.acm_nodes + možnost zadat přímou IPv6/IPv4 adresu.
 * Prázdná hodnota = lokální ACM (fallback).
 *
 * Použití:
 *   <NodeSelector value={form.target_node} onChange={v => setForm(f => ({...f, target_node: v}))} />
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_API_PORT } from '../lib/node-ports';
import { Select } from './ui/Select';
import { Server, Wifi } from 'lucide-react';
import { useMeshPeers } from '../widgets/NodePicker';
import { nodeDisplayLabel, routingTarget } from '../widgets/node-targets';

interface NodeSelectorProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  /** Dovolí zadat přímou IPv6/IPv4 adresu místo výběru ze seznamu */
  allowDirect?: boolean;
  className?: string;
}

export function NodeSelector({
  value,
  onChange,
  label = 'ACM Node',
  allowDirect = true,
  className = '',
}: NodeSelectorProps) {
  const { t } = useTranslation();
  const [customMode, setCustomMode] = useState(false);

  const { data: peers } = useMeshPeers({ includeLocal: false });
  const nodes = (peers ?? []).filter((n) => n.enabled !== false);

  const inp =
    `w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500 ${className}`;

  const wireOf = (n: (typeof nodes)[number]) => routingTarget(n);
  const selectedNode = nodes.find(
    (n) =>
      wireOf(n) === value || n.name === value || n.label === value ||
      n.route_name === value || n.host === value,
  );

  return (
    <div className='space-y-1.5'>
      <div className='flex items-center justify-between'>
        <div className='text-xs text-gray-400'>{label}</div>
        {allowDirect && (
          <button
            type='button'
            onClick={() => {
              setCustomMode(!customMode);
              onChange('');
            }}
            className='text-[10px] text-blue-400 hover:text-blue-300 transition-colors'
          >
            {customMode
              ? t('servers.fromList', '← Ze seznamu')
              : t('servers.directAddress', 'Přímá adresa →')}
          </button>
        )}
      </div>

      {customMode
        ? (
          <input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={`fd00::1 nebo [fd00::1]:${DEFAULT_API_PORT} nebo 10.0.0.1`}
            className={inp}
          />
        )
        : (
          <Select
            value={selectedNode ? wireOf(selectedNode) : value}
            onChange={(e) => onChange(e.target.value)}
            className={className}
          >
            <option value=''>{t('servers.localDefault', '— Lokální ACM (výchozí) —')}</option>
            {nodes.map((n) => (
              <option key={wireOf(n)} value={wireOf(n)}>
                {nodeDisplayLabel(n)}{n.host ? ` · ${n.host}` : ''}
              </option>
            ))}
          </Select>
        )}

      {value && !customMode && selectedNode && (
        <div className='text-[10px] text-gray-600 flex items-center gap-1'>
          <Server size={9} />
          {selectedNode.host || nodeDisplayLabel(selectedNode)}
        </div>
      )}

      {value && customMode && (
        <div className='text-[10px] text-gray-600 flex items-center gap-1'>
          <Wifi size={9} />
          {t(
            'servers.directHint',
            'Přímé připojení — klíč z registru, pokud je tam adresa zaregistrovaná',
          )}
        </div>
      )}

      {!value && nodes.length === 0 && (
        <div className='text-[10px] text-amber-500/70'>
          {t('servers.noRegisteredNodes', 'Žádné registrované nody — použije se lokální ACM')}
        </div>
      )}
    </div>
  );
}
